import {
  getRedirectionTargets,
  getRedirectionTarget,
  createRedirectionTarget,
  updateRedirectionTarget,
  deleteRedirectionTarget,
} from '@/services/redirection-targets-service/redirection-targets-service';
import {
  CreateRedirectionTargetPayload,
  UpdateRedirectionTargetPayload,
  RedirectionTargetsQueryParams,
} from '@/services/redirection-targets-service/redirection-targets-service.types';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useRedirectionTargets = (params?: RedirectionTargetsQueryParams) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['redirection-targets', params],
    queryFn: () => getRedirectionTargets(params),
    enabled: !!params?.company_id,
  });

  return {
    redirectionTargets: data?.redirection_targets || [],
    totalCount: data?.redirection_targets_count || 0,
    isLoading,
    error,
  };
};

export const useRedirectionTarget = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ReactQueryKeys.REDIRECTION_TARGET(id),
    queryFn: () => getRedirectionTarget(id),
    enabled: !!id,
  });

  return {
    redirectionTarget: data,
    isLoading,
    error,
  };
};

export const useCreateRedirectionTarget = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: ({ companyId, payload }: { companyId: number; payload: CreateRedirectionTargetPayload }) =>
      createRedirectionTarget(companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['redirection-targets'],
      });
    },
  });

  return {
    createRedirectionTarget: mutate,
    createRedirectionTargetAsync: mutateAsync,
    isPending,
  };
};

export const useUpdateRedirectionTarget = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, companyId, payload }: { id: number; companyId: number; payload: UpdateRedirectionTargetPayload }) =>
      updateRedirectionTarget(id, companyId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['redirection-targets'],
      });
      queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.REDIRECTION_TARGET(variables.id),
      });
    },
  });

  return {
    updateRedirectionTarget: mutate,
    updateRedirectionTargetAsync: mutateAsync,
    isPending,
  };
};

export const useDeleteRedirectionTarget = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, companyId }: { id: number; companyId?: number }) =>
      deleteRedirectionTarget(id, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['redirection-targets'],
      });
    },
  });

  return {
    deleteRedirectionTarget: mutate,
    deleteRedirectionTargetAsync: mutateAsync,
    isPending,
  };
};
